import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Plus, Users } from 'lucide-react';
import { adminApi } from '../api/auth';
import type { AdminUnitRow, FoundingInvite, InviteSummary } from '../api/auth';
import { Button } from '../ui/Button';
import type { Me } from '../api/auth';
import { AppHeader } from '../ui/AppHeader';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { CopyLink } from '../ui/CopyLink';
import { EmptyState } from '../ui/EmptyState';
import { TextField } from '../ui/Field';

export function AdminInvitesScreen({ me, onNavigate }: { me: Me; onNavigate: (path: string) => void }) {
  const [invites, setInvites] = useState<InviteSummary[] | null>(null);
  const [units, setUnits] = useState<AdminUnitRow[]>([]);
  const [fresh, setFresh] = useState<FoundingInvite | null>(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, u] = await Promise.all([adminApi.listInvites(), adminApi.listUnits()]);
      setInvites(i);
      setUnits(u);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setFresh(await adminApi.createInvite(email.trim(), note.trim() || null));
      setEmail('');
      setNote('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create an invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <AppHeader me={me} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Platform administration
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-3)' }}
            >
              Invites
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '52ch' }}
            >
              {/* "Nothing is emailed automatically yet" was true until TROOP-223 and is the kind
                  of sentence that stays on a screen for a year after it stops being. */}
              An invite lets one person create one unit and become its first admin. It is emailed
              to the address you name, and only that address can use it.
            </p>

            {/* A system admin has no unit of their own, so this page is genuinely
                all they can reach until they use an invite. Saying so beats leaving
                them to work out why every other route bounces them back here. */}
            {me.memberships.length === 0 && (
              <p
                className="t-body"
                style={{
                  color: 'var(--color-text-primary)',
                  background: 'var(--color-status-compliant-surface)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3) var(--space-4)',
                  margin: 'var(--space-4) 0 0',
                  maxWidth: '52ch',
                }}
              >
                <strong>Setting up your own unit?</strong> Send yourself an invite below &mdash;
                your own address &mdash; then open the link. You&rsquo;ll name the unit and become
                its admin, and the rest of the app opens up from there.
              </p>
            )}
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <div
              style={{
                background: 'var(--color-surface-raised)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--elevation-raised)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-6)',
              }}
            >
              <form onSubmit={create} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
                <h2 className="t-subheading" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
                  New invite
                </h2>

                <TextField
                  label="Send it to"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  // Required since TROOP-224, where this said "Recommended" and meant it. An
                  // unbound invite was a bearer token -- whoever opened the URL founded a unit as
                  // themselves -- and the server refuses one now, so a hint that still offered the
                  // choice would be offering something the API rejects.
                  hint="Only this address can use the invite. Whoever opens it also has to hold the mailbox."
                />

                <TextField
                  label="Note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  hint="For you — who this went to, and why."
                />

                {error && (
                  <p
                    className="t-body"
                    role="alert"
                    style={{
                      background: 'var(--color-status-blocked-surface)',
                      border: '1px solid var(--color-status-blocked)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3)',
                      color: 'var(--color-text-primary)',
                      margin: 0,
                    }}
                  >
                    {error}
                  </p>
                )}

                <div>
                  <Button variant="primary" type="submit">
                    <Plus size={16} aria-hidden="true" />
                    {busy ? 'Sending…' : 'Send invite'}
                  </Button>
                </div>
              </form>

              {fresh && (
                <div
                  style={{
                    marginTop: 'var(--space-5)',
                    paddingTop: 'var(--space-5)',
                    borderTop: '1px solid var(--color-border-subtle)',
                  }}
                >
                  <CopyLink url={fresh.url} label={`A copy of what ${fresh.restrictedToEmail} was sent`} />
                  <p
                    className="t-caption"
                    style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
                  >
                    {/* Stated plainly because it is true and cannot be undone: the raw
                        token is never stored, so this is the only time it exists. Copy-link stays
                        after TROOP-223 for the admin setting up their own unit -- they open it
                        themselves rather than going to their inbox -- and it is safe to keep now
                        that possession of the URL is not sufficient (TROOP-224). */}
                    Emailed. Copy it now if you want your own &mdash; this link is shown once and
                    cannot be retrieved later. Expires{' '}
                    {new Date(fresh.expiresAt).toLocaleDateString()}.
                  </p>
                </div>
              )}
            </div>

            <h2
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-3)' }}
            >
              Issued
            </h2>

            {invites?.length === 0 && (
              <EmptyState
                icon={CalendarDays}
                headline="No invites yet"
                explanation="Name the address of whoever is setting up a unit, and we email it to them."
              />
            )}

            <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
              {invites?.map((invite) => (
                <div
                  key={invite.id}
                  style={{
                    background: 'var(--color-surface-raised)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--elevation-raised)',
                    padding: 'var(--space-4)',
                  }}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                      {/* Truthiness rather than `??`, because the row this falls back for carries
                          an empty string and not a null (TROOP-224's migration writes '' where
                          there was no address). A `??` here rendered a blank line for exactly the
                          invites worth naming. It states the fact and leaves the consequence to
                          `Status`, which already says Revoked — a legacy invite that was *claimed*
                          before the migration is not revoked, and a label saying so would be
                          wrong on precisely the rows that already worked. */}
                      {invite.restrictedToEmail || 'No address on file'}
                    </span>
                    <Status invite={invite} />
                  </div>

                  {invite.note && (
                    <p
                      className="t-caption"
                      style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
                    >
                      {invite.note}
                    </p>
                  )}

                  <p
                    className="t-caption tabular"
                    style={{ color: 'var(--color-text-muted)', margin: 'var(--space-1) 0 0' }}
                  >
                    Issued {new Date(invite.issuedAt).toLocaleDateString()} &middot; expires{' '}
                    {new Date(invite.expiresAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>

            <h2
              className="t-label"
              style={{ color: 'var(--color-text-muted)', margin: 'var(--space-7) 0 var(--space-3)' }}
            >
              Units
            </h2>

            {units.length === 0 ? (
              <EmptyState
                icon={Users}
                headline="No units yet"
                explanation="A unit appears here once someone uses an invite to create one."
              />
            ) : (
              <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
                {units.map((unit) => (
                  <div
                    key={unit.id}
                    className="flex items-baseline justify-between gap-3"
                    style={{
                      background: 'var(--color-surface-raised)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-3) var(--space-4)',
                      boxShadow: 'var(--elevation-raised)',
                    }}
                  >
                    <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                      {unit.name}
                    </span>
                    <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
                      {unit.memberCount} members
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Said out loud, because an admin surface that lists units invites the
                assumption that it can open them. */}
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-4)' }}
            >
              Names and counts only. Seeing a unit here does not give access to its roster or its
              conversations &mdash; that needs a membership in the unit.
            </p>
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

function Status({ invite }: { invite: InviteSummary }) {
  if (invite.revoked) {
    return <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>Revoked</span>;
  }

  if (invite.claimedAt) {
    return (
      <span className="t-caption" style={{ color: 'var(--color-status-compliant)' }}>
        Used{invite.resultingUnitName ? ` · ${invite.resultingUnitName}` : ''}
      </span>
    );
  }

  if (new Date(invite.expiresAt) < new Date()) {
    return <span className="t-caption" style={{ color: 'var(--color-text-muted)' }}>Expired</span>;
  }

  return (
    <span className="t-caption" style={{ color: 'var(--color-accent-announcement-text)' }}>
      Waiting
    </span>
  );
}
